import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { M365User, M365UserDetailResponse } from '../types/m365Users'
import { getCustomerGraphClientId } from '../config/authConfig'
import {
  UserDetailHeader,
  ProfileSection,
  LicensesSection,
  GroupsSection,
  DevicesSection,
  ActivitySection,
  MailSection,
  OneDriveSection,
  AuthenticationSection,
} from '../components/m365users'

type TabType = 'profile' | 'licenses' | 'authentication' | 'mail' | 'onedrive' | 'activity' | 'groups' | 'devices'

interface CustomerInfo {
  id: string
  name: string
  partnerCenterId?: string
}

export default function M365UserDetail() {
  const { customerId, userId } = useParams<{ customerId: string; userId: string }>()
  const navigate = useNavigate()
  const { getAccessToken, getCustomerGraphAccessToken } = useAuth()

  const [user, setUser] = useState<M365User | null>(null)
  const [customer, setCustomer] = useState<CustomerInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('profile')
  const [authenticating, setAuthenticating] = useState(false)
  const [needsAuthorization, setNeedsAuthorization] = useState(false)

  // Store the customer tenant ID and token
  const customerTenantIdRef = useRef<string | null>(null)
  const customerTenantTokenRef = useRef<string | null>(null)
  const authInProgressRef = useRef<Promise<string> | null>(null)

  // Get the customer tenant token (cached or fresh)
  const getCustomerToken = useCallback(async (): Promise<string> => {
    if (customerTenantTokenRef.current) {
      return customerTenantTokenRef.current
    }

    if (authInProgressRef.current) {
      return authInProgressRef.current
    }

    const authPromise = (async () => {
      const partnerToken = await getAccessToken()

      if (!customerTenantIdRef.current) {
        const tenantInfo = await api.getM365TenantId(partnerToken, customerId!)
        customerTenantIdRef.current = tenantInfo.tenantId
      }

      if (customerTenantIdRef.current) {
        setAuthenticating(true)
        try {
          customerTenantTokenRef.current = await getCustomerGraphAccessToken(
            customerTenantIdRef.current,
            { silentOnly: false }
          )
        } finally {
          setAuthenticating(false)
        }
      }

      if (!customerTenantTokenRef.current) {
        throw new Error('Failed to authenticate to customer tenant')
      }

      return customerTenantTokenRef.current
    })()

    authInProgressRef.current = authPromise

    try {
      return await authPromise
    } finally {
      authInProgressRef.current = null
    }
  }, [customerId, getAccessToken, getCustomerGraphAccessToken])

  // Fetch user and customer data
  const fetchData = useCallback(async () => {
    if (!customerId || !userId) return

    try {
      setLoading(true)
      setError(null)

      const partnerToken = await getAccessToken()

      // Fetch customer info first
      const customerData = await api.getSetupCustomer(partnerToken, customerId)
      if (!customerData.customer) {
        setError('Customer not found')
        return
      }

      const pcMapping = customerData.customer.mappings?.find(
        (m: { integrationId: string }) => m.integrationId === 'partner-center'
      )

      setCustomer({
        id: customerId,
        name: customerData.customer.name,
        partnerCenterId: pcMapping?.externalId,
      })

      if (!pcMapping?.externalId) {
        setError('Customer is not linked to Partner Center')
        return
      }

      // Get customer tenant token
      const token = await getCustomerToken()

      // Fetch user data - API returns { user, licenses }
      const response = await api.getM365User(token, customerId, userId) as M365UserDetailResponse
      setUser(response.user)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user data'
      const fullErrorString = JSON.stringify(err)
      const hasAuthError = errorMessage.includes('AADSTS90099') ||
        errorMessage.includes('has not been authorized') ||
        errorMessage.includes('NEEDS_AUTHORIZATION') ||
        fullErrorString.includes('AADSTS90099')

      if (hasAuthError || errorMessage.includes('user_cancelled')) {
        setNeedsAuthorization(true)
        setError('The Partner License Portal app needs to be authorized for this customer tenant.')
      } else if (errorMessage.includes('not found')) {
        setError('User not found')
      } else {
        setError(errorMessage)
      }
      customerTenantTokenRef.current = null
    } finally {
      setLoading(false)
    }
  }, [customerId, userId, getAccessToken, getCustomerToken])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Build the admin consent URL
  const getAdminConsentUrl = () => {
    if (!customerTenantIdRef.current) return null
    const clientId = getCustomerGraphClientId()
    const redirectUri = encodeURIComponent(window.location.origin + '/auth-callback')
    return `https://login.microsoftonline.com/${customerTenantIdRef.current}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`
  }

  const handleAuthorize = async () => {
    if (!customerTenantIdRef.current) {
      try {
        const partnerToken = await getAccessToken()
        const tenantInfo = await api.getM365TenantId(partnerToken, customerId!)
        customerTenantIdRef.current = tenantInfo.tenantId
      } catch {
        setError('Failed to get customer tenant ID')
        return
      }
    }

    const consentUrl = getAdminConsentUrl()
    if (consentUrl) {
      window.open(consentUrl, '_blank')
      setError(null)
    }
  }

  const handleRetryAuth = async () => {
    setNeedsAuthorization(false)
    customerTenantTokenRef.current = null
    await fetchData()
  }

  const handleUserUpdated = (updatedUser: M365User) => {
    setUser(updatedUser)
  }

  if (loading || authenticating) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">
              {authenticating ? 'Authenticating to customer tenant...' : 'Loading user details...'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !user || !customer) {
    return (
      <div className="px-4 sm:px-0">
        <div className={`rounded-md ${needsAuthorization ? 'bg-yellow-50' : 'bg-red-50'} p-4`}>
          <div className="flex">
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${needsAuthorization ? 'text-yellow-800' : 'text-red-800'}`}>
                {needsAuthorization ? 'Authorization Required' : 'Error'}
              </h3>
              <div className={`mt-2 text-sm ${needsAuthorization ? 'text-yellow-700' : 'text-red-700'}`}>
                <p>{error || 'User not found'}</p>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => navigate(`/customers/${customerId}`)}
                  className={`text-sm font-medium ${needsAuthorization ? 'text-yellow-800 hover:text-yellow-700' : 'text-red-800 hover:text-red-700'}`}
                >
                  Back to Customer
                </button>
                {needsAuthorization && (
                  <>
                    <button
                      onClick={handleRetryAuth}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-yellow-600 text-sm font-medium rounded text-yellow-700 hover:bg-yellow-50"
                    >
                      Retry
                    </button>
                    <button
                      onClick={handleAuthorize}
                      className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-sm font-medium rounded text-white hover:bg-yellow-700"
                    >
                      Grant Consent
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const tabs: { key: TabType; label: string; icon: JSX.Element }[] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      key: 'licenses',
      label: 'Licenses',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
    },
    {
      key: 'authentication',
      label: 'Authentication',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      key: 'mail',
      label: 'Mail',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: 'onedrive',
      label: 'OneDrive',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
    },
    {
      key: 'activity',
      label: 'Activity',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
    },
    {
      key: 'groups',
      label: 'Groups',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      key: 'devices',
      label: 'Devices',
      icon: (
        <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="px-4 sm:px-0">
      {/* Back button */}
      <div className="mb-4">
        <Link
          to={`/customers/${customerId}`}
          className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {customer.name}
        </Link>
      </div>

      {/* User header */}
      <UserDetailHeader
        user={user}
        customerId={customerId!}
        customerTenantId={customerTenantIdRef.current || ''}
        onUserUpdated={handleUserUpdated}
      />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mt-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'profile' && (
          <ProfileSection
            user={user}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
            onUserUpdated={handleUserUpdated}
          />
        )}
        {activeTab === 'licenses' && (
          <LicensesSection
            user={user}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
            onLicenseUpdated={() => fetchData()}
          />
        )}
        {activeTab === 'authentication' && (
          <AuthenticationSection
            userId={userId!}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
        {activeTab === 'mail' && (
          <MailSection
            userId={userId!}
            userPrincipalName={user.userPrincipalName}
            displayName={user.displayName}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
        {activeTab === 'onedrive' && (
          <OneDriveSection
            userId={userId!}
            userPrincipalName={user.userPrincipalName}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
        {activeTab === 'activity' && (
          <ActivitySection
            userId={userId!}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
        {activeTab === 'groups' && (
          <GroupsSection
            userId={userId!}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
        {activeTab === 'devices' && (
          <DevicesSection
            userId={userId!}
            customerId={customerId!}
            customerTenantId={customerTenantIdRef.current || ''}
          />
        )}
      </div>
    </div>
  )
}
